// 메일 본문 iframe 처리 정책 factory. 뷰어 settings(keepSrcDocForIFrame, domainsForIFrame)로
// common-web `getSafeElement`/`sanitizeHtmlForIframeWrite` 에 주입할 sanitizer 를 만든다.
//
// OMD-210: 외부 iframe 의 자동 src 로드가 (desktop) 메인 webview on_navigation 을 타고 OS 브라우저로
// 새는 문제 때문에, 허용 도메인(자체 서버 officemail) 외 iframe 과 srcdoc-only iframe 을 제거한다.
// web 은 settings 미주입(domainsForIFrame 없음)이면 factory 가 undefined 를 반환해 iframe 미관여.

interface MailBodyIframeSanitizerSettings {
  keepSrcDocForIFrame?: boolean
  domainsForIFrame?: string[]
}

// src(상대/절대)가 https 이고 호스트가 허용 도메인(정확히 일치 또는 서브도메인)인지 판정한다.
// host 가 아니라 hostname 사용(포트 제외) — desktop 원본 정책(officemailIframePolicy)과 동치.
function isAllowedFrameSrc(src: string | null, domains: string[]): boolean {
  if (src == null || src === '') return false
  try {
    // base 를 두어 protocol-relative(`//host/...`)·상대경로를 https 기준으로 해석한다. 상대경로는
    // base host(invalid.example)가 되어 자연히 거부되고, data:/blob: 등은 protocol 검사에서 걸러진다.
    const url = new URL(src, 'https://invalid.example/')
    if (url.protocol !== 'https:') return false
    const host = url.hostname.toLowerCase()
    return domains.some((d) => host === d || host.endsWith('.' + d))
  } catch {
    // 잘못된 src 는 new URL 이 throw → fail-closed(제거).
    return false
  }
}

export function makeMailBodyIframeSanitizer(
  settings: MailBodyIframeSanitizerSettings,
): ((iframe: HTMLIFrameElement) => void) | undefined {
  // 빈/공백 도메인 원소 제거 + 소문자 정규화. 빈 문자열이 남으면 endsWith('.' + '') = endsWith('.')
  // 로 임의 호스트가 전면 허용되는 false-allow 가 생기므로 반드시 걸러낸다.
  const domains = settings.domainsForIFrame
    ?.map((d) => d?.trim().toLowerCase())
    .filter((d): d is string => d != null && d !== '')
  // 도메인 목록이 없거나 비면 iframe 미관여(undefined 반환 → 호출부가 sanitizer 미주입, iframe 유지).
  if (domains == null || domains.length === 0) return undefined

  return (iframe: HTMLIFrameElement): void => {
    // srcdoc 은 임의 인라인 HTML 을 렌더해 src allowlist 를 우회하므로 기본 무력화한다.
    if (settings.keepSrcDocForIFrame !== true) {
      iframe.removeAttribute('srcdoc')
    }
    // src 부재(srcdoc-only 포함)·비허용 도메인·non-https 는 모두 제거.
    if (!isAllowedFrameSrc(iframe.getAttribute('src'), domains)) {
      iframe.remove()
    }
  }
}
